import { Transition } from '@headlessui/react';
import { AnimatePresence } from 'framer-motion';
import React, {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import {
  AddUserIcon,
  ChevronDownIcon,
  CloseIcon,
  EditIcon,
  ExcerciseIcon,
  ExportIcon,
  FilterIcon,
  LockIcon,
  PlusIcon,
  SearchIcon,
  UploadIcon,
} from '../../assets/icons/index';
import { AlertDialogWrapper } from '../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import Button from '../../components/button/index';
import { Checkbox } from '../../components/checkbox/index';
import { Input } from '../../components/input/index';
import { LoadingCrossfade } from '../../components/loading-skeleton/index';
import { CrossfadePanel } from '../../components/motion/index';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/popover/index';
import { Select, SelectButton, SelectOption, SelectOptions } from '../../components/select/index';
import { Sheet, SheetContent, SheetTrigger } from '../../components/sheet/index';
import { QueryKey } from '../../constants/index';
import { sideBarContext } from '../../context/side-bar-context';
import {
  AnalyticHooks,
  Stakeholder as StakeholderHook,
  useDebounce,
  useEventOutsideElement,
  useHasLoadedOnce,
  useInvalidateQueries,
  useLocale,
  useModalState,
  usePagination,
  useQueryParams,
  useScreenSize,
  useSelectedCompany,
} from '../../hooks/index';
import { stakeholdersSortStore } from '../../storage/local/index';
import {
  arrayOfStakeholderTypeTranslation,
  stakeholderTypeTranslation,
} from '../../translations/stakeholder-translation.util';
import { PossibleAction } from '../../types/google-analytics-types.types';
import { SubscriptionTypeTitle } from '../../types/plan.types';
import { ExerciseType } from '../../types/pool-plans.types';
import {
  FilterVariant,
  filterVariant,
  filterVariantMap,
  itemsToFetchMap,
  Stakeholder,
  StakeholderOrder,
  StakeholderRole,
  StakeholderSortBy,
  StakeholderType,
} from '../../types/stakeholder.types';
import { toNumber } from '../../utils/to-number.util';
import { CompanySettingsCardTab } from '../company-settings/company-settings.types';
import { toCompanySettingsPath } from '../company-settings/company-settings-access.util';
import { StakeholdersCard, TerminatedCard } from './cards/index';
import CancelInvitationModal from './modals/cancel-invite-modal';
import DeleteStakeholderModal from './modals/delete-stakeholder-modal';
import GrantAccessModal from './modals/grant-access-modal';
import MakeContributorModal from './modals/make-contributor-modal';
import ResendInvitationModal from './modals/resend-invite-modal';
import RevokeAccessModal from './modals/revoke-access-modal';
import UpgradePlanModal from './modals/upgrade-plan-modal';
import AddStakeholderModal from './stakeholder-actions/add-stakeholder-modal';
import {
  BulkImportStakeholdersModal,
  BulkModifyStakeholdersModal,
} from './stakeholder-actions/bulk-actions/index';
import BulkActionsModal from './stakeholder-actions/bulk-actions-modal';
import EditStakeholderModal from './stakeholder-actions/edit-stakeholder-modal/index';
import { EditStakeholderVariant } from './stakeholder-actions/stakeholder-actions.types';
import {
  EditStakeholderTerminationModal,
  TerminateStakeholderModal,
} from './stakeholder-actions/terminate-stakeholder-modal/index';
import { StakeholderTableProps } from './stakeholder-table/stakeholder-table';
import { TerminatedExerciseModal } from './stakeholder-terminated-table/terminated-exercise/index';
import { TerminatedExerciseForm } from './stakeholder-terminated-table/terminated-exercise/terminated-exercise-form/index';
import {
  arrayOfStakeholderCardTab,
  StakeholderCard,
  stakeholderCardTranslation,
} from './stakeholders.types';
import {
  StakeholdersPlaceholder,
  StakeholdersTablePlaceholder,
} from './stakeholders-loading-placeholders';

export type StakeholdersCardComponentProps = {
  currentPage: number;
  handlePageChange: (page: number) => void;
  itemsToFetch: number;
  onFetchAmountChange: (amount: number) => void;
  totalPages: number;
} & Omit<StakeholderTableProps, 'className'>;

const stakeholdersCardComponentVariant: Record<
  StakeholderCard,
  (props: StakeholdersCardComponentProps) => ReactNode
> = {
  [StakeholderCard.STAKEHOLDERS]: (props) => <StakeholdersCard {...props} />,
  [StakeholderCard.TERMINATED]: (props) => <TerminatedCard {...props} />,
};

export const transformDateToISOString = (year: number, month: number, day: number) =>
  new Date(Date.UTC(year, month - 1, day)).toISOString();

export type SelectedStakeholder = {
  id: string;
  isAccessRevoked: boolean;
  isActive: boolean;
  isInvited: boolean;
  role: StakeholderRole;
};

const sortByTypeValues = Object.values(StakeholderType);

const Stakeholders: FC = () => {
  const [selectedTypeFilters, setTypeFilters] = useState<StakeholderType[]>(sortByTypeValues);

  const isSelectedAllTypeFilters = useMemo(
    () => selectedTypeFilters.length === sortByTypeValues.length,
    [selectedTypeFilters],
  );

  const isEmpty = false;
  const [selectedFilter, setSelectedFilter] = useState<FilterVariant>('all');
  const [sortState, setSortState] = useState<{
    field: null | StakeholderSortBy;
    sortMode: null | StakeholderOrder;
  }>(stakeholdersSortStore.get() || { field: null, sortMode: null });

  const [selectedStakeholderIds, setSelectedStakeholderIds] = useState<string[]>([]);
  const [selectedStakeholder, setStakeholder] = useState<{
    hasSubEmails?: boolean;
    stakeholderId: string;
    subEmail?: string;
  }>({ stakeholderId: '', subEmail: undefined, hasSubEmails: false });
  const [selectedCard, setCard] = useState<StakeholderCard>(StakeholderCard.STAKEHOLDERS);

  const { get } = useQueryParams();
  const navigate = useNavigate();

  const { selectedCompany } = useSelectedCompany();
  const companyId = selectedCompany?.id || '';

  const [search, setSearch] = useState<null | string>(null);
  const [isOpenSearch, setOpenSearch] = useState(false);
  const [itemsToFetch, setItemsToFetch] = useState(itemsToFetchMap[1]);

  const [isFocusedInput, setInputFocus] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);

  const [currentPage, setCurrentPage] = usePagination('stakeholders');

  const { width } = useScreenSize();
  useEventOutsideElement(inputRef, 'click', () => !search && setOpenSearch(false));

  const [isOpenAddStakeholderModal, setOpenStakeholderModal] = useState(!!get('openAddModal'));
  const [isOpenEditStakeholderModal, setOpenEditStakeholderModal] = useState<{
    isOpen: boolean;
    mode: 'EDIT' | 'VIEW';
    stakeholderId: string;
    step: EditStakeholderVariant;
  }>({
    mode: 'VIEW',
    stakeholderId: '',
    isOpen: false,
    step: 1,
  });

  const { isOpen: isSideBarExpanded } = useContext(sideBarContext);

  const { invalidateQuery } = useInvalidateQueries(
    QueryKey.GET_STAKEHOLDERS,
    QueryKey.GET_STAKEHOLDER,
  );

  const { debounceCallback } = useDebounce({
    callback: () => {
      invalidateQuery();
    },
  });

  const { messagesLocale } = useLocale();
  const deleteStakeholderTracker = AnalyticHooks.useClick(PossibleAction.DELETE_STAKEHOLDER);
  const { deleteStakeholder } = StakeholderHook.useDelete();
  const { invite, cancelInvitation } = StakeholderHook.useInvitation();
  const { toggleAccess } = StakeholderHook.useToggleAccess();
  const { toggleStatus } = StakeholderHook.useToggleStatus();

  const exportStakeholderTracker = AnalyticHooks.useClick(PossibleAction.DOWNLOAD_FILE);
  const { exportStakeholders } = StakeholderHook.useExport();

  const { refetch, stakeholders, isLoading, totalStakeholders, totalPages } =
    StakeholderHook.useStakeholders({
      companyId,
      filter: selectedFilter,
      orderBy: sortState.sortMode,
      currentPage,
      sortBy: sortState.field,
      search: search || '',
      searchType: selectedTypeFilters,
      numberOfFetchedItems: toNumber(itemsToFetch),
      isTerminated: selectedCard === StakeholderCard.TERMINATED,
      loadUsers: true,
    });

  // Full skeleton on first load only; later sort/filter/search/tab reloads skeleton the table.
  const hasLoadedOnce = useHasLoadedOnce(isLoading);

  const handlePageChange = useCallback(
    (pageNumber = 1) => {
      setCurrentPage(pageNumber);
    },
    [setCurrentPage],
  );

  const handleFilterSelect = useCallback(
    (filter: FilterVariant) => {
      setSelectedFilter(filter);
      handlePageChange();
    },
    [handlePageChange],
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value);
      debounceCallback(value);
    },
    [debounceCallback],
  );

  const handleSortField = useCallback(
    (newField: StakeholderSortBy) => {
      setSortState((prevState) => {
        if (prevState.field === newField) {
          const nextSortMode = !prevState.sortMode
            ? StakeholderOrder.ASC
            : prevState.sortMode === StakeholderOrder.ASC
              ? StakeholderOrder.DESC
              : null;

          stakeholdersSortStore.set({ ...prevState, sortMode: nextSortMode });
          return { ...prevState, sortMode: nextSortMode };
        } else {
          stakeholdersSortStore.set({
            field: newField,
            sortMode: StakeholderOrder.ASC,
          });
          return { field: newField, sortMode: StakeholderOrder.ASC };
        }
      });
      invalidateQuery();
    },
    [invalidateQuery],
  );

  useEffect(() => {
    if (!search && companyId) {
      refetch();
    }
  }, [companyId, refetch, search]);

  const {
    isOpen: isOpenDeleteModal,
    handleDismissModal: handleDismissDeleteModal,
    handleSuccessModal: handleSuccessDeleteModal,
    handleOpenModal: handleOpenDeleteModal,
  } = useModalState<{ stakeholderId: string; subEmail?: string }>({
    onSuccess: (successData) => {
      successData &&
        deleteStakeholder(
          { companyId, ...successData },
          {
            onSuccess: () => {
              deleteStakeholderTracker();
              toast.success(messagesLocale['STAKEHOLDER_DELETED_TOAST']);
              invalidateQuery();
              if (stakeholders?.length === 1) setCurrentPage((prev) => (prev > 1 ? prev - 1 : 1));
            },
          },
        );
    },
  });

  const {
    isOpen: isOpenCancelInvitationModal,
    handleDismissModal: handleDismissCancelInvitationModal,
    handleSuccessModal: handleSuccessCancelInvitationModal,
    handleOpenModal: handleOpenCancelInvitationModal,
  } = useModalState<{ stakeholderId: string; subEmail?: string }>({
    onSuccess: (successData) =>
      successData &&
      cancelInvitation(successData, {
        onSuccess: () => {
          invalidateQuery();
        },
      }),
  });

  const {
    isOpen: isOpenResendInvitationModal,
    handleDismissModal: handleDismissResendInvitationModal,
    handleSuccessModal: handleSuccessResendInvitationModal,
    handleOpenModal: handleOpenResendInvitationModal,
  } = useModalState<{ stakeholderId: string; subEmail?: string }>({
    onSuccess: (successData) => successData && invite(successData),
  });

  const {
    isOpen: isOpenRevokeAccessModal,
    handleDismissModal: handleDismissRevokeAccessModal,
    handleSuccessModal: handleSuccessRevokeAccessModal,
    handleOpenModal: handleOpenRevokeAccessModal,
  } = useModalState<string>({
    onSuccess: (stakeholderId) =>
      stakeholderId &&
      toggleAccess(
        { stakeholderId },
        {
          onSuccess: () => {
            invalidateQuery();
          },
        },
      ),
  });

  const {
    isOpen: isOpenGrantAccessModal,
    handleDismissModal: handleDismissGrantAccessModal,
    handleSuccessModal: handleSuccessGrantAccessModal,
    handleOpenModal: handleOpenGrantAccessModal,
  } = useModalState<string>({
    onSuccess: (stakeholderId) =>
      stakeholderId &&
      toggleAccess(
        { stakeholderId },
        {
          onSuccess: () => {
            invalidateQuery();
          },
        },
      ),
  });

  const {
    isOpen: isOpenMakeContributorModal,
    handleDismissModal: handleDismissMakeContributorModal,
    handleSuccessModal: handleSuccessMakeContributorModal,
    handleOpenModal: handleOpenMakeContributorModal,
  } = useModalState<string>({
    onSuccess: (stakeholderId) =>
      stakeholderId &&
      toggleStatus(
        { companyId, stakeholderId },
        {
          onSuccess: () => {
            invalidateQuery();
            toast.success('Role successfully changed');
          },
        },
      ),
  });

  const {
    isOpen: isOpenUpgradePlanModal,
    handleDismissModal: handleDismissUpgradePlanModal,
    handleSuccessModal: handleSuccessUpgradePlanModal,
    handleOpenModal: handleOpenUpgradePlanModal,
  } = useModalState({
    onSuccess: () => {
      navigate(toCompanySettingsPath(CompanySettingsCardTab.COMPANY_PLAN_BILLING));
    },
  });

  const {
    isOpen: isOpenBulkImportModal,
    handleDismissModal: handleDismissBulkImportModal,
    handleOpenModal: handleOpenBulkImportModal,
  } = useModalState({});

  const {
    isOpen: isOpenBulkModifyModal,
    handleDismissModal: handleDismissBulkModifyModal,
    handleOpenModal: handleOpenBulkModifyModal,
  } = useModalState({});

  const {
    isOpen: isOpenBulkActionsModal,
    handleDismissModal: handleDismissBulkActionsModal,
    handleOpenModal: handleOpenBulkActionsModal,
    handleSuccessModal: handleSuccessBulkActionsModal,
  } = useModalState({
    onSuccess: () => setSelectedStakeholderIds([]),
  });

  const {
    handleCloseModal: handleCloseTerminateModal,
    handleOpenModal: handleOpenTerminateModal,
    isOpen: isOpenTerminateModal,
    modalData: stakeholdersToTerminate,
  } = useModalState<unknown, unknown, Stakeholder[]>();

  const {
    handleCloseModal: handleCloseEditTerminationModal,
    handleOpenModal: handleOpenEditTerminationModal,
    isOpen: isOpenEditTerminationModal,
    modalData: stakeholdersToEditTermination,
  } = useModalState<unknown, unknown, Stakeholder[]>();

  const {
    handleCloseModal: handleCloseExerciseModal,
    handleOpenModal: handleOpenExerciseModal,
    isOpen: isOpenExerciseModal,
    toggler: exerciseModalToggler,
  } = useModalState({});

  const {
    handleCloseModal: handleCloseExerciseStockModal,
    handleOpenModal: handleOpenExerciseStockModal,
    isOpen: isOpenExerciseStockModal,
    modalData: exerciseType,
  } = useModalState<unknown, unknown, ExerciseType>();

  const { bulkActions } = StakeholderHook.useBulkActions();

  const isCanAddStakeholders = useMemo(
    () => (selectedCompany?.stakeholdersLimit || 0) > totalStakeholders,
    [selectedCompany, totalStakeholders],
  );
  const isMobile = useMemo(() => width <= 1023, [width]);
  const isTablet = useMemo(() => width <= 1233, [width]);
  const showMobileSearchBar = useMemo(
    () => isTablet || (width <= 1300 && isSideBarExpanded),
    [width, isTablet, isSideBarExpanded],
  );

  const stripePlanType =
    selectedCompany?.latestSubscriptionData?.items?.data[0]?.price?.product?.metadata?.type;

  useEffect(() => {
    if (selectedStakeholderIds.length !== 0) return handleOpenBulkActionsModal();
    handleDismissBulkActionsModal();
  }, [handleDismissBulkActionsModal, handleOpenBulkActionsModal, selectedStakeholderIds.length]);

  return (
    <LoadingCrossfade
      // A missing company gates the full skeleton unconditionally: hasLoadedOnce may only
      // downgrade the *stakeholders reload* skeleton (sort/filter/search/tab), which the table
      // renders itself below. Folding it into the company check rendered the page with no company
      // whenever /companies/selected was still in flight or failed (white screen on reload after
      // an admin subscription change, or after removing every stakeholder).
      isLoading={!selectedCompany || (isLoading && !hasLoadedOnce)}
      skeleton={<StakeholdersPlaceholder />}
    >
      {() => (
        <>
          <UpgradePlanModal
            isOpen={isOpenUpgradePlanModal}
            onDismiss={handleDismissUpgradePlanModal}
            onSuccess={handleSuccessUpgradePlanModal}
            planName={
              stripePlanType ? (
                <AppFormattedMessage id={SubscriptionTypeTitle[stripePlanType]} />
              ) : (
                ''
              )
            }
            stakeholderRole={selectedCompany?.roleInCompany}
          />
          <DeleteStakeholderModal
            hasMultipleEmails={selectedStakeholder.hasSubEmails}
            isOpen={isOpenDeleteModal}
            onDismiss={() => handleDismissDeleteModal()}
            onSuccess={() => handleSuccessDeleteModal(selectedStakeholder)}
            subEmailAction={Boolean(selectedStakeholder.subEmail)}
          />
          <CancelInvitationModal
            isOpen={isOpenCancelInvitationModal}
            onDismiss={() => handleDismissCancelInvitationModal()}
            onSuccess={() => handleSuccessCancelInvitationModal(selectedStakeholder)}
          />
          <ResendInvitationModal
            isOpen={isOpenResendInvitationModal}
            onDismiss={() => handleDismissResendInvitationModal()}
            onSuccess={() => handleSuccessResendInvitationModal(selectedStakeholder)}
          />

          <MakeContributorModal
            hasMultipleEmails={selectedStakeholder.hasSubEmails}
            isOpen={isOpenMakeContributorModal}
            onDismiss={() => handleDismissMakeContributorModal()}
            onSuccess={() => handleSuccessMakeContributorModal(selectedStakeholder.stakeholderId)}
          />

          <RevokeAccessModal
            hasMultipleEmails={selectedStakeholder.hasSubEmails}
            isOpen={isOpenRevokeAccessModal}
            onDismiss={() => handleDismissRevokeAccessModal()}
            onSuccess={() => handleSuccessRevokeAccessModal(selectedStakeholder.stakeholderId)}
          />
          <GrantAccessModal
            isOpen={isOpenGrantAccessModal}
            onDismiss={() => handleDismissGrantAccessModal()}
            onSuccess={() => handleSuccessGrantAccessModal(selectedStakeholder.stakeholderId)}
          />

          <BulkImportStakeholdersModal
            companyId={companyId || ''}
            invalidateQuery={invalidateQuery}
            isOpenModal={isOpenBulkImportModal}
            setOpen={handleDismissBulkImportModal}
          />

          <BulkModifyStakeholdersModal
            companyId={companyId || ''}
            invalidateQuery={invalidateQuery}
            isOpenModal={isOpenBulkModifyModal}
            setOpen={handleDismissBulkModifyModal}
          />

          <AddStakeholderModal
            companyId={companyId || ''}
            invalidateQuery={invalidateQuery}
            isOpenModal={isOpenAddStakeholderModal}
            setOpen={setOpenStakeholderModal}
          />

          <EditStakeholderModal
            companyId={companyId || ''}
            invalidateQuery={invalidateQuery}
            isDefaultEditing={isOpenEditStakeholderModal?.mode === 'EDIT'}
            isOpenModal={isOpenEditStakeholderModal?.isOpen || false}
            onEditClick={({ id, step }) =>
              setOpenEditStakeholderModal({ stakeholderId: id, mode: 'EDIT', step, isOpen: true })
            }
            setClose={() =>
              setOpenEditStakeholderModal({
                mode: 'VIEW',
                isOpen: false,
                stakeholderId: '',
                step: 3,
              })
            }
            stakeholderId={isOpenEditStakeholderModal?.stakeholderId || ''}
            step={isOpenEditStakeholderModal?.step || 1}
          />
          <BulkActionsModal
            handleCancelInvitation={({ id: stakeholderIds, onSuccess }) =>
              bulkActions(
                {
                  companyId,
                  action: 'CANCEL_INVITATION',
                  stakeholderIds,
                },
                { onSuccess },
              )
            }
            handleDelete={({ id: stakeholderIds, onSuccess }) =>
              bulkActions({ action: 'DELETE', stakeholderIds, companyId }, { onSuccess })
            }
            handleGrantAccess={({ id: stakeholderIds, onSuccess }) =>
              bulkActions(
                {
                  companyId,
                  action: 'TOGGLE_ACCESS',
                  stakeholderIds,
                },
                { onSuccess },
              )
            }
            handleMakeContributor={({ id: stakeholderIds, onSuccess }) =>
              bulkActions(
                {
                  companyId,
                  action: 'TOGGLE_CONTRIBUTOR',
                  stakeholderIds,
                },
                { onSuccess },
              )
            }
            handleOpenEditTerminationModal={handleOpenEditTerminationModal}
            handleOpenTerminateModal={handleOpenTerminateModal}
            handleRemoveContributorRole={({ id: stakeholderIds, onSuccess }) =>
              bulkActions(
                {
                  action: 'TOGGLE_CONTRIBUTOR',
                  stakeholderIds,
                  companyId,
                },
                { onSuccess },
              )
            }
            handleResendInvitation={({ id: stakeholderIds, onSuccess }) =>
              bulkActions(
                { action: 'INVITE', stakeholderIds, companyId },
                {
                  onSuccess,
                },
              )
            }
            handleRevokeAccess={({ id: stakeholderIds, onSuccess }) =>
              bulkActions(
                {
                  action: 'TOGGLE_ACCESS',
                  stakeholderIds,
                  companyId,
                },
                { onSuccess },
              )
            }
            handleSendInvitation={({ id: stakeholderIds, onSuccess }) =>
              bulkActions(
                { action: 'INVITE', stakeholderIds, companyId },
                {
                  onSuccess,
                },
              )
            }
            handleSuccess={handleSuccessBulkActionsModal}
            invalidateQuery={invalidateQuery}
            isOpenModal={isOpenBulkActionsModal}
            onRemoveSelectAll={() => setSelectedStakeholderIds([])}
            selectedStakeholderIds={selectedStakeholderIds}
            stakeholders={stakeholders || []}
          />
          <TerminateStakeholderModal
            companyId={companyId}
            handleClose={handleCloseTerminateModal}
            invalidateQuery={invalidateQuery}
            isOpen={isOpenTerminateModal}
            setSelectedStakeholderIds={setSelectedStakeholderIds}
            stakeholders={stakeholdersToTerminate}
          />
          <EditStakeholderTerminationModal
            companyId={companyId}
            handleClose={handleCloseEditTerminationModal}
            invalidateQuery={invalidateQuery}
            isOpen={isOpenEditTerminationModal}
            setSelectedStakeholderIds={setSelectedStakeholderIds}
            stakeholders={stakeholdersToEditTermination}
          />
          <AlertDialogWrapper
            control={{ onOpenChange: exerciseModalToggler, open: isOpenExerciseModal }}
          >
            <TerminatedExerciseModal
              onClose={handleCloseExerciseModal}
              onOpenExerciseStockModal={handleOpenExerciseStockModal}
            />
          </AlertDialogWrapper>
          <TerminatedExerciseForm
            companyId={companyId}
            handleClose={handleCloseExerciseStockModal}
            invalidateQuery={invalidateQuery}
            isOpen={isOpenExerciseStockModal}
            type={exerciseType}
          />

          <div className="flex h-fit min-h-[200px] w-full flex-col gap-4 overflow-hidden rounded-md lg:p-4 lg:shadow-sm">
            <div className="flex h-[38px] w-full gap-4 border-b-[1px] border-gray-200">
              {arrayOfStakeholderCardTab.map(([cardKey, card]) => (
                <Button
                  className={twMerge(
                    'flex h-full w-fit rounded-none border-b-2 border-transparent text-sm font-[450] text-gray-400',
                    selectedCard === cardKey && 'border-brand-700 font-[550] text-brand-700',
                  )}
                  key={card}
                  onClick={() => {
                    setCard(cardKey);
                    setCurrentPage(1);
                  }}
                  styleType="NONE"
                >
                  <AppFormattedMessage id={card} />
                </Button>
              ))}
            </div>
            <div className="flex h-fit min-h-full w-full flex-col rounded-md max-lg:gap-4 lg:rounded-md lg:shadow-sm">
              {(stakeholders?.length || selectedCard === StakeholderCard.STAKEHOLDERS) && (
                <div className="flex max-h-16 w-full items-center justify-between gap-4 lg:p-4">
                  <span className="text-base font-[550] text-[#172335] max-lg:hidden">
                    <AppFormattedMessage
                      id={stakeholderCardTranslation[selectedCard][selectedFilter]}
                    />
                  </span>
                  <Select onChange={handleFilterSelect} value={selectedFilter}>
                    {({ open }) => (
                      <>
                        <div className="relative rounded border-gray-100 px-[6px] py-2 lg:hidden">
                          <SelectButton className="flex justify-between border-gray-100 bg-gray-50 p-3 px-[6px]">
                            <span className="text-sm font-[450] text-[#344054]">
                              <AppFormattedMessage id={filterVariant[selectedFilter]} />
                            </span>
                            <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                          </SelectButton>

                          <Transition
                            as="div"
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                            show={open}
                          >
                            <SelectOptions>
                              {filterVariantMap.map(([filterKey, filter]) => (
                                <SelectOption key={filterKey} value={filterKey}>
                                  {({ selected }) => (
                                    <span
                                      className={twMerge(
                                        selected ? 'font-[450]' : 'font-normal',
                                        'block text-sm text-gray-700',
                                      )}
                                    >
                                      <AppFormattedMessage id={filter} />
                                    </span>
                                  )}
                                </SelectOption>
                              ))}
                            </SelectOptions>
                          </Transition>
                        </div>
                      </>
                    )}
                  </Select>
                  <div className="relative flex h-full w-fit items-center gap-3">
                    <Sheet modal={false} open={showMobileSearchBar && isOpenSearch}>
                      <div className="flex items-center justify-end" ref={inputRef}>
                        <Input
                          inputClassName="h-9"
                          onBlur={() => setInputFocus(false)}
                          onChange={handleSearch}
                          onFocus={() => {
                            setInputFocus(true);
                          }}
                          placeholder={
                            search || isFocusedInput ? '' : <AppFormattedMessage id="SEARCH" />
                          }
                          value={search || ''}
                          wrapperClassName={twMerge(
                            'transition-all duration-500 w-[200px] overflow-hidden h-9',
                            isOpenSearch ? 'delay-500' : 'w-0',
                            showMobileSearchBar && 'w-0',
                          )}
                        />
                        {!showMobileSearchBar && isOpenSearch && typeof search === 'string' && (
                          <div
                            className="absolute mr-8 cursor-pointer p-2"
                            onClick={() => {
                              setSearch('');
                              invalidateQuery();
                            }}
                          >
                            <CloseIcon />
                          </div>
                        )}
                        <SheetTrigger
                          className={twMerge(
                            'flex size-9 items-center justify-center rounded border-[1px] border-gray-100 bg-gray-50 transition-all duration-300',
                            !showMobileSearchBar && isOpenSearch
                              ? 'h-9 rounded-l-none rounded-br-none border-x-0 border-t-0 border-b-black'
                              : 'delay-1000',
                            !showMobileSearchBar &&
                              isFocusedInput &&
                              'border-b-[2px] border-b-brand-700 transition-none',
                          )}
                          onClick={() => {
                            setOpenSearch((prev) => !prev);
                            setSearch(null);
                            invalidateQuery();
                          }}
                        >
                          <SearchIcon iconColor={isFocusedInput ? '#2565C8' : '#344054'} />
                        </SheetTrigger>
                      </div>
                      <SheetContent
                        className="w-full"
                        onClick={(e) => e.stopPropagation()}
                        onInteractOutside={() => {
                          if (!search) {
                            setOpenSearch(false);
                            setSearch(null);
                            invalidateQuery();
                          }
                        }}
                        side="TOP"
                      >
                        <div className={twMerge('flex')}>
                          <Input
                            icon={search && <CloseIcon />}
                            onBlur={() => setInputFocus(false)}
                            onChange={handleSearch}
                            onFocus={(e) => {
                              setInputFocus(true);
                              e.stopPropagation();
                            }}
                            onIconClick={() => {
                              setTimeout(() => {
                                setSearch('');
                                invalidateQuery();
                              }, 0);
                            }}
                            placeholder={<AppFormattedMessage id="SEARCH" />}
                            value={search || ''}
                            wrapperClassName={twMerge('transition-all duration-1000 w-full')}
                          />
                        </div>
                      </SheetContent>
                    </Sheet>
                    <Select onChange={handleFilterSelect} value={selectedFilter}>
                      {({ open }) => (
                        <>
                          <div className="relative rounded border-gray-100 px-[6px] py-2 max-lg:hidden">
                            <SelectButton className="flex justify-between border-gray-100 bg-gray-50 p-3 px-[6px]">
                              <span className="text-sm font-[450] text-[#344054]">
                                <AppFormattedMessage id={filterVariant[selectedFilter]} />
                              </span>
                              <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                            </SelectButton>

                            <Transition
                              as="div"
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              show={open}
                            >
                              <SelectOptions>
                                {filterVariantMap.map(([filterKey, filter]) => (
                                  <SelectOption key={filterKey} value={filterKey}>
                                    {({ selected }) => (
                                      <span
                                        className={twMerge(
                                          selected ? 'font-[450]' : 'font-normal',
                                          'block text-sm text-gray-700',
                                        )}
                                      >
                                        <AppFormattedMessage id={filter} />
                                      </span>
                                    )}
                                  </SelectOption>
                                ))}
                              </SelectOptions>
                            </Transition>
                          </div>
                        </>
                      )}
                    </Select>

                    {!isEmpty && (
                      <div className="flex h-9 items-center gap-2">
                        <Popover>
                          <PopoverTrigger className="h-full w-fit rounded border bg-gray-50">
                            <div className="flex items-center justify-between gap-2 rounded border-gray-200 px-3">
                              <FilterIcon className="lg:hidden" />
                              <span className="text-sm font-[450] text-gray-700 max-lg:hidden">
                                {isSelectedAllTypeFilters ||
                                !stakeholderTypeTranslation[selectedTypeFilters[0]] ? (
                                  <AppFormattedMessage id="ALL_TYPES" />
                                ) : (
                                  `${messagesLocale[stakeholderTypeTranslation[selectedTypeFilters[0]]]} ${selectedTypeFilters.length - 1 < 1 ? '' : `+ ${selectedTypeFilters.length - 1}  more`}`
                                )}
                              </span>
                              <ChevronDownIcon className="h-4 w-4 text-gray-400 max-lg:hidden" />
                            </div>
                          </PopoverTrigger>
                          <PopoverContent className="flex w-fit flex-col gap-[2px] rounded p-2 font-normal shadow-2xl [&>*]:font-inter [&>*]:font-normal">
                            <div className="flex w-full items-center gap-2 rounded p-[6px] pr-8 text-sm text-gray-700">
                              <Checkbox
                                checked={isSelectedAllTypeFilters}
                                onChange={() => {
                                  handlePageChange();
                                  setTypeFilters(isSelectedAllTypeFilters ? [] : sortByTypeValues);
                                }}
                              />
                              <span className="text-sm font-normal text-gray-700">
                                <AppFormattedMessage id="ALL_TYPES" />
                              </span>
                            </div>

                            {arrayOfStakeholderTypeTranslation.map(([sortKey, sortTitle]) => (
                              <div
                                className="flex w-full items-center gap-2 rounded p-[6px] pr-8 text-sm text-gray-700"
                                key={sortKey}
                              >
                                <Checkbox
                                  checked={selectedTypeFilters.includes(sortKey)}
                                  onChange={(state) => {
                                    setTypeFilters((prev) =>
                                      state
                                        ? [...prev, sortKey]
                                        : prev.filter((sortVariant) => sortVariant !== sortKey),
                                    );
                                    handlePageChange();
                                  }}
                                />
                                <span className="text-sm font-normal text-gray-700">
                                  {messagesLocale[sortTitle]}
                                </span>
                              </div>
                            ))}
                          </PopoverContent>
                        </Popover>
                      </div>
                    )}

                    <>
                      {selectedCard === StakeholderCard.STAKEHOLDERS ? (
                        <Button
                          className="flex size-9 gap-1 border-[1px] border-gray-100 bg-gray-25 px-3 py-[6px] max-lg:hidden lg:w-fit"
                          data-permission-required
                          onClick={handleOpenBulkModifyModal}
                        >
                          <EditIcon />
                          <span className="text-sm font-[450] text-gray-700 max-lg:hidden">
                            {isSideBarExpanded && isTablet ? (
                              ''
                            ) : (
                              <AppFormattedMessage id="BULK_MODIFY" />
                            )}
                          </span>
                        </Button>
                      ) : (
                        <>
                          {!isTablet && (
                            <Button
                              className="flex size-9 gap-1 border-[1px] border-gray-100 bg-gray-25 px-3 py-[6px] lg:w-fit"
                              data-permission-required
                              onClick={handleOpenExerciseModal}
                            >
                              <ExcerciseIcon />
                              <span className="text-sm font-[450] text-gray-700 max-lg:hidden">
                                <AppFormattedMessage id="EXERCISE_RETURN_TO_POOL" />
                              </span>
                            </Button>
                          )}
                        </>
                      )}
                      <Button
                        className="flex size-9 gap-1 border-[1px] border-gray-100 bg-gray-25 px-3 py-[6px] lg:w-fit"
                        onClick={() =>
                          exportStakeholders(
                            { companyId },
                            { onSuccess: () => exportStakeholderTracker('Stakeholders') },
                          )
                        }
                      >
                        <ExportIcon className="max-lg:rotate-180" />
                        <span className="text-sm font-[450] text-gray-700 max-lg:hidden">
                          {isSideBarExpanded && isTablet ? '' : <AppFormattedMessage id="EXPORT" />}
                        </span>
                      </Button>
                      {selectedCard === StakeholderCard.STAKEHOLDERS && (
                        <Button
                          className="flex size-9 gap-1 border-[1px] border-gray-100 bg-gray-25 px-3 py-[6px] max-lg:hidden lg:w-fit"
                          data-permission-required
                          onClick={handleOpenBulkImportModal}
                        >
                          <UploadIcon />
                          <span className="text-sm font-[450] text-gray-700">
                            {isSideBarExpanded && isTablet ? (
                              ''
                            ) : (
                              <AppFormattedMessage id="IMPORT" />
                            )}
                          </span>
                        </Button>
                      )}
                      {selectedCard === StakeholderCard.STAKEHOLDERS && (
                        <Button
                          className={twMerge(
                            'flex size-9 gap-1 px-4 py-[6px] lg:w-fit',
                            !isCanAddStakeholders && 'bg-gray-400',
                          )}
                          data-permission-required
                          onClick={() =>
                            isCanAddStakeholders
                              ? setOpenStakeholderModal(true)
                              : handleOpenUpgradePlanModal()
                          }
                        >
                          {isCanAddStakeholders ? (
                            isMobile ? (
                              <PlusIcon />
                            ) : (
                              <AddUserIcon />
                            )
                          ) : (
                            <LockIcon iconColor="#ffffff" />
                          )}
                          <span className={twMerge('text-sm font-[450] text-white max-lg:hidden')}>
                            {isSideBarExpanded && isTablet ? (
                              ''
                            ) : (
                              <AppFormattedMessage id="ADD_STAKEHOLDER" />
                            )}
                          </span>
                        </Button>
                      )}
                    </>
                  </div>
                </div>
              )}
              <div className="h-[1px] w-full bg-[#F2F2F2] lg:hidden" />
              <div
                className={twMerge('flex h-full w-full flex-col justify-between gap-4 pb-4')}
                style={{ minHeight: !isMobile ? 'calc(100vh - 270px)' : undefined }}
              >
                <AnimatePresence mode="wait">
                  <CrossfadePanel
                    className="flex h-full w-full flex-col"
                    key={`${selectedCard}-${isLoading ? 'loading' : 'ready'}`}
                    panelKey={`${selectedCard}-${isLoading ? 'loading' : 'ready'}`}
                    translate={false}
                  >
                    {isLoading ? (
                      <StakeholdersTablePlaceholder />
                    ) : (
                      stakeholdersCardComponentVariant[selectedCard]({
                        currentPage,
                        handlePageChange,
                        totalPages: totalPages || 0,
                        accountId: selectedCompany?.stakeholder?.id || '',
                        roleInCompany: selectedCompany?.roleInCompany,
                        isSelectStarted: selectedStakeholderIds.length !== 0,
                        onRemoveSelectAll: () => setSelectedStakeholderIds([]),
                        onSelectAll: (stakeholders) => {
                          setSelectedStakeholderIds(stakeholders);
                        },
                        isSelected: (selectedId) => {
                          return !!selectedStakeholderIds.find((id) => id === selectedId);
                        },
                        onSelect: (stakeholderId) => {
                          setSelectedStakeholderIds((prev) => [...prev, stakeholderId]);
                        },
                        onRemoveSelect: (selectedId) =>
                          setSelectedStakeholderIds((prev) =>
                            prev.filter((id) => id !== selectedId),
                          ),
                        sortField: sortState.field,
                        sortMode: sortState.sortMode,
                        searchValue: search || '',
                        handleCancelInvitation: (stakeholderId, subEmail) => {
                          setStakeholder({ stakeholderId, subEmail });
                          handleOpenCancelInvitationModal();
                        },
                        handleGrantAccess: (stakeholderId, hasSubEmails) => {
                          setStakeholder({ stakeholderId, hasSubEmails });
                          handleOpenGrantAccessModal();
                        },
                        handleMakeContributor: (stakeholderId, hasSubEmails) => {
                          setStakeholder({ stakeholderId, hasSubEmails });
                          handleOpenMakeContributorModal();
                        },
                        handleRemoveContributorRole: (stakeholderId, hasSubEmails) => {
                          setStakeholder({ stakeholderId, hasSubEmails });
                          toggleStatus(
                            { stakeholderId, companyId },
                            {
                              onSuccess: () => {
                                invalidateQuery();
                                toast.success('Role successfully changed');
                              },
                            },
                          );
                        },
                        handleResendInvitation: (stakeholderId, subEmail) => {
                          setStakeholder({ stakeholderId, subEmail });
                          handleOpenResendInvitationModal();
                        },
                        handleSendInvitation: (stakeholderId, subEmail) => {
                          setStakeholder({ stakeholderId, subEmail });
                          invite(
                            { stakeholderId, subEmail },
                            {
                              onSuccess: () => {
                                invalidateQuery();
                              },
                            },
                          );
                        },
                        handleRevokeAccess: (stakeholderId, hasSubEmails) => {
                          setStakeholder({ stakeholderId, hasSubEmails });
                          handleOpenRevokeAccessModal();
                        },
                        handleTerminate: (stakeholderId) =>
                          handleOpenTerminateModal(
                            stakeholders?.filter((stakeholder) => stakeholder.id === stakeholderId),
                          ),
                        handleEditTermination: (stakeholderId) =>
                          handleOpenEditTerminationModal(
                            stakeholders?.filter((stakeholder) => stakeholder.id === stakeholderId),
                          ),
                        handleView: (stakeholderId) =>
                          setOpenEditStakeholderModal({
                            mode: 'VIEW',
                            stakeholderId,
                            isOpen: true,
                            step: 3,
                          }),
                        handleDelete: (stakeholderId, subEmail, hasSubEmails) => {
                          setStakeholder({ stakeholderId, subEmail, hasSubEmails });
                          handleOpenDeleteModal();
                        },
                        handleEdit: (stakeholderId) =>
                          setOpenEditStakeholderModal({
                            mode: 'EDIT',
                            stakeholderId,
                            isOpen: true,
                            step: 3,
                          }),
                        onSortClick: (newField) => handleSortField(newField),
                        data: stakeholders,
                        onFetchAmountChange: setItemsToFetch,
                        itemsToFetch,
                      })
                    )}
                  </CrossfadePanel>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </>
      )}
    </LoadingCrossfade>
  );
};

export default Stakeholders;
